import { animate, style, transition, trigger } from '@angular/animations';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { CanColor, mixinColor } from '@angular/material/core';
import { Constructor } from '@mtna/core-ts';
import { Option, OptionSet, PackagingFormat } from '@rds/rds-packaging-ts';

import { COMPLEX_DECELERATING, DURATION_SMALL, EASING_ACCELERATING } from '../core/animations';

/**
 * Change event for when selection changes on a RdsPackagingOptionComponent.
 */
export class RdsPackagingOptionChangeEvent {
  /**
   * Creates an instance of RdsPackagingOptionChangeEvent.
   * @param selected Whether the option is selected
   * @param format packaging format
   * @param optionSet packaging format options
   */
  constructor(public selected: boolean, public format: PackagingFormat, public optionSet: OptionSet) {}
}

export class RdsPackagingOptionBase {
  constructor(public _elementRef: ElementRef) {}
}

export const _RdsPackagingOptionMixinBase: Constructor<CanColor> & typeof RdsPackagingOptionBase = mixinColor(
  RdsPackagingOptionBase,
  'primary'
);

/**
 * Displays a signle packaging format with any given options.
 * Can be selected or deselected. Once selcted, the options become disabled.
 * If the user wants to change the options, they will need to deselect it first.
 *
 * @author Will Davis <will.davis@mtna.us>
 */
@Component({
  selector: 'rds-c-packaging-option',
  templateUrl: './packaging-option.component.html',
  styleUrls: ['./packaging-option.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('appear', [
      transition(':enter', [
        style({ transform: 'rotate(-90deg) scale(0) translateY(-50%)' }),
        animate(COMPLEX_DECELERATING, style({ transform: 'rotate(0deg) scale(1) translateY(-50%)' })),
      ]),
      transition(':leave', [animate(`${DURATION_SMALL} ${EASING_ACCELERATING}`, style({ opacity: 0 }))]),
    ]),
  ],
})
export class RdsPackagingOptionComponent extends _RdsPackagingOptionMixinBase implements CanColor, OnInit {
  @HostBinding('class.selected') isSelected = false;

  /** Theme color, defaults to primary */
  @Input() color: 'primary' | 'accent' = 'primary';

  /** Required packaging format */
  @Input() format!: PackagingFormat;

  /** Required option set for this packaging format. Describes any additional options. */
  @Input() optionSet!: OptionSet;

  /** Whether this packaging format is selected. If it is selected, the form becomes disabled. */
  @Input()
  get selected(): boolean {
    return this._selected;
  }
  set selected(value: boolean) {
    const newValue = coerceBooleanProperty(value);
    if (newValue !== this.selected) {
      this._selected = newValue;
      this.isSelected = this._selected;
      if (this.form) {
        this._selected ? this._disableFormControls() : this._enableFormControls();
      }
      this.cdr.markForCheck();
    }
  }
  private _selected = false;

  /** Fired when this format is selected or deselected */
  @Output() valueChange = new EventEmitter<RdsPackagingOptionChangeEvent>();

  /** Internal reactive form for the format's options */
  form!: FormGroup;

  constructor(private cdr: ChangeDetectorRef, elementRef: ElementRef) {
    super(elementRef);
  }

  ngOnInit() {
    // Initialize form, create a FormControl for every Option
    const group: {
      [key: string]: AbstractControl;
    } = {};
    if (this._hasOptions()) {
      this.optionSet.options?.forEach((option: Option) => {
        group[option.id] = new FormControl(
          { value: this._getInitialFormValue(option), disabled: this.selected },
          option.required ? Validators.required : null
        );
      });
    }
    this.form = new FormGroup(group);
  }

  /**
   * When the form is "Submitted", the user is toggling the selected state of the format.
   * Flip the value of the selected property, extract new values from the form, emit a change event.
   */
  toggleSelected() {
    if (!this.selected && this.form?.invalid) {
      return;
    }
    const newSelectedValue = !this.selected;
    const formValue = this.form?.value;
    const updatedOptionSet: OptionSet = {
      ...this.optionSet,
      options: this._hasOptions() ? this.optionSet.options?.map((option) => ({ ...option, value: formValue[option.id] })) : undefined,
    };

    this.valueChange.emit(new RdsPackagingOptionChangeEvent(newSelectedValue, this.format, updatedOptionSet));
    // Setting selected, will markForCheck
    this.selected = newSelectedValue;
  }

  _trackByOption(index: number, option: Option) {
    return !option ? null : option.id;
  }

  private _disableFormControls() {
    if (this._hasOptions()) {
      for (const option of this.optionSet.options as Option[]) {
        this.form?.get(option.id)?.disable();
      }
    }
  }

  private _enableFormControls() {
    if (this._hasOptions()) {
      for (const option of this.optionSet.options as Option[]) {
        this.form?.get(option.id)?.enable();
      }
    }
  }

  /**
   * Gets the initial form value for a given `Option`.
   * If it has a default value use that, otherwise depending on
   * the data type, give an initial default value.
   * @param option option from which to get the initial form value
   * @returns an initial or default value
   */
  private _getInitialFormValue(option: Option): unknown {
    const hasValue: boolean = option.value !== null && option.value !== undefined;
    if (hasValue) {
      return option.value;
    }
    switch (option.dataType) {
      case 'BOOLEAN':
        return false;
      default:
        return '';
    }
  }

  /**
   * @returns Whether the option set has any options
   */
  private _hasOptions(): boolean {
    return !!this.optionSet && !!this.optionSet.options && !!this.optionSet.options.length;
  }
}
